import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router";
import { useAllowedPages } from "@/hooks/use-rbac";
import { filterAllowedNavItems } from "@shared/rbac";
import { FlaskConical, Search, ArrowRight } from "lucide-react";
import { QA_PRODUCTS } from "@shared/products";
// #914 (Akira's "search seems inconsistent" catch): pages derive from the sidebar
// catalog — see palette-items.ts for the derivation + the drift story.
import { PAGE_ITEMS, type SearchItem } from "@/lib/palette-items";

const PRODUCT_ITEMS: SearchItem[] = Object.entries(QA_PRODUCTS).map(([slug, config]) => ({
  label: config.label,
  path: `/quality?tab=charts&product=${slug}`,
  category: "product" as const,
  icon: FlaskConical,
}));

const ALL_ITEMS = [...PAGE_ITEMS, ...PRODUCT_ITEMS];

/**
 * #914: lets other chrome (the sidebar's quick-search affordance) open the palette
 * without prop-drilling through AppShell — the palette listens for this window event
 * alongside its existing global Cmd/Ctrl+K handler.
 */
export const OPEN_PALETTE_EVENT = "crosbe:open-palette";
export function openCommandPalette() {
  window.dispatchEvent(new Event(OPEN_PALETTE_EVENT));
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // #599: only surface pages this role can actually open. The route guard +
  // server RBAC already enforce access; this stops the palette advertising
  // pages (incl. admin) a role can't reach, and the confusing bounce on click.
  const pages = useAllowedPages();
  const visiblePageItems = useMemo(() => filterAllowedNavItems(PAGE_ITEMS, pages), [pages]);
  const visibleAllItems = useMemo(() => filterAllowedNavItems(ALL_ITEMS, pages), [pages]);

  const filtered = query.trim()
    ? visibleAllItems.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 12)
    : visiblePageItems.slice(0, 8);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setQuery("");
    setSelectedIndex(0);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  const handleSelect = useCallback(
    (item: SearchItem) => {
      navigate(item.path);
      handleClose();
    },
    [navigate, handleClose]
  );

  // Global Ctrl+K / Cmd+K listener
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (open) handleClose();
        else handleOpen();
      }
      if (e.key === "Escape" && open) {
        handleClose();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener(OPEN_PALETTE_EVENT, handleOpen);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(OPEN_PALETTE_EVENT, handleOpen);
    };
  }, [open, handleOpen, handleClose]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      // Small delay to let the modal render
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Keyboard navigation
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && filtered[selectedIndex]) {
        e.preventDefault();
        handleSelect(filtered[selectedIndex]);
      }
    },
    [filtered, selectedIndex, handleSelect]
  );

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!open) return null;

  const groupedPages = filtered.filter((i) => i.category === "page");
  const groupedProducts = filtered.filter((i) => i.category === "product");
  let flatIndex = -1;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-0 top-[20%] z-50 mx-auto w-full max-w-lg px-4">
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
          {/* Search input */}
          <div className="flex items-center gap-3 border-b border-border px-4">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Search pages, products..."
              className="flex-1 bg-transparent py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none"
            />
            <kbd className="hidden sm:inline rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto p-2">
            {filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No results found
              </p>
            ) : (
              <>
                {groupedPages.length > 0 && (
                  <div>
                    <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                      Pages
                    </p>
                    {groupedPages.map((item) => {
                      flatIndex++;
                      const idx = flatIndex;
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleSelect(item)}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            selectedIndex === idx
                              ? "bg-primary text-primary-foreground"
                              : "text-foreground hover:bg-muted"
                          }`}
                        >
                          {Icon && <Icon className="h-4 w-4 shrink-0" />}
                          <span className="flex-1 text-left">{item.label}</span>
                          <ArrowRight className="h-3 w-3 shrink-0 opacity-50" />
                        </button>
                      );
                    })}
                  </div>
                )}
                {groupedProducts.length > 0 && (
                  <div>
                    <p className="mt-2 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                      Products
                    </p>
                    {groupedProducts.map((item) => {
                      flatIndex++;
                      const idx = flatIndex;
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleSelect(item)}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            selectedIndex === idx
                              ? "bg-primary text-primary-foreground"
                              : "text-foreground hover:bg-muted"
                          }`}
                        >
                          {Icon && <Icon className="h-4 w-4 shrink-0" />}
                          <span className="flex-1 text-left">{item.label}</span>
                          <ArrowRight className="h-3 w-3 shrink-0 opacity-50" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer hint */}
          <div className="border-t border-border px-4 py-2 text-[10px] text-muted-foreground">
            <span className="hidden sm:inline">
              <kbd className="rounded border border-border bg-muted px-1 py-0.5">↑↓</kbd> navigate
              {" "}
              <kbd className="rounded border border-border bg-muted px-1 py-0.5">↵</kbd> select
              {" "}
              <kbd className="rounded border border-border bg-muted px-1 py-0.5">esc</kbd> close
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
